import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CategoryTag from "@/components/base/CategoryTag";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { getStoreBySlug, getRelatedPostsForStore } from "@/api/content";
import { canonical, localBusinessSchema } from "@/utils/seo";
import type { Store, Section, PostCard } from "@/types/content";

export default function StoreDetailPage({ initialData }: { initialData?: Store | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [store, setStore] = useState<Store | null>(initialData ?? null);
  const [relatedPosts, setRelatedPosts] = useState<PostCard[]>([]);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    getStoreBySlug(slug).then((data) => {
      if (data) {
        setStore(data);
        getRelatedPostsForStore(data.category, data.slug, 3).then((posts) => {
          setRelatedPosts(posts);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!store) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          找不到此店家
        </h2>
        <p className="text-slate-400 mb-4">該探店頁面不存在或已被移除</p>
        <a
          href="/stores"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          返回探店列表
        </a>
      </div>
    );
  }

  const bodySections = store.content.filter(
    (s) => s._type !== "hero" && s._type !== "pricingPackage" && s._type !== "cta",
  );
  const allSections: Section[] = [
    ...bodySections,
    ...(relatedPosts.length > 0
      ? [
          {
            _type: "relatedPosts" as const,
            _key: "auto-related",
            title: "你可能也喜歡",
            posts: relatedPosts,
          },
        ]
      : []),
  ];

  const baseMeta = store.seo || {
    title: `${store.name}｜美好時光指南探店`,
    description: store.description,
    ogImage: store.coverImage,
  };
  const meta = {
    ...baseMeta,
    canonical: baseMeta.canonical || canonical(`/stores/${store.slug}`),
    keywords: baseMeta.keywords || [store.category, ...store.tags, "探店", "評比", "美好時光指南"],
    structuredData: baseMeta.structuredData || localBusinessSchema(store.name, store.description, `/stores/${store.slug}`, store.coverImage, store.rating, store.reviewCount, store.location),
  };

  return (
    <>
      <SEO meta={meta} />

      {/* ========== Store Header ========== */}
      <div className="w-full relative">
        <div className="w-full h-[280px] md:h-[400px] overflow-hidden">
          <img
            fetchpriority="high"
            loading="eager"
            src={store.coverImage}
            alt={store.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-8 pb-8 md:pb-10">
            <div className="max-w-4xl mx-auto">
              <CategoryTag label={store.category} variant="dark" size="md" className="mb-3" />
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                {store.name}
              </h1>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/90">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400" />
                  <span className="font-semibold">{store.rating}</span>
                  <span>({store.reviewCount} 則評價)</span>
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line" />
                  </div>
                  {store.location}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Tags Bar */}
      {store.features.length > 0 && (
        <div className="w-full border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-500">店家特色：</span>
              {store.features.map((feature) => (
                <span
                  key={feature}
                  className="px-3 py-1 bg-primary/5 border border-primary/10 rounded-full text-xs font-medium text-primary whitespace-nowrap"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Store Body */}
      <div className="w-full">
        <SectionRenderer sections={allSections} />
      </div>
    </>
  );
}